import { useState, useRef } from "react";
import {
    Button,
    Divider,
    Image as AntdImage,
    InputNumber,
    message,
    Space,
    Typography,
    Switch,
    Flex,
    Card,
} from "antd";
import { downloadImage } from "../pdfEditor/utils";
import GeneralUploader from "../pdfEditor/Uploader";

const { Title, Text } = Typography;
const MIN_DIM = 50;

export default function ImageResize() {
    const [file, setFile] = useState(null);
    const [imageSrc, setImageSrc] = useState(null);
    const [width, setWidth] = useState(300);
    const [height, setHeight] = useState(200);
    const [keepAspect, setKeepAspect] = useState(true);
    const aspectRef = useRef(null);

    const [processedDataUrl, setProcessedDataUrl] = useState(null);
    const processed = Boolean(processedDataUrl);

    const resetAll = () => {
        setFile(null);
        setImageSrc(null);
        setWidth(300);
        setHeight(200);
        setKeepAspect(true);
        aspectRef.current = null;
        setProcessedDataUrl(null);
    };

    const handleUploadChange = (info) => {
        const uploaded = Array.isArray(info) ? info[0] : info?.file ?? info;
        if (!uploaded || uploaded.status === "uploading") return;

        const actual = uploaded.originFileObj ?? uploaded;
        if (!actual || typeof actual.arrayBuffer !== "function") {
            message.error("Please upload a valid image file");
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            const src = e.target.result;
            setImageSrc(src);
            setFile(actual);
            setProcessedDataUrl(null);

            const img = new window.Image();
            img.onload = () => {
                aspectRef.current =
                    img.naturalWidth / img.naturalHeight || null;
                if (keepAspect && aspectRef.current) {
                    setHeight(
                        Math.max(MIN_DIM, Math.round(width / aspectRef.current))
                    );
                }
            };
            img.src = src;
        };
        reader.readAsDataURL(actual);
    };

    const handleWidthChange = (val) => {
        if (val == null) return;
        const newW = Number(val);
        setWidth(newW);
        if (keepAspect && aspectRef.current) {
            setHeight(Math.max(MIN_DIM, Math.round(newW / aspectRef.current)));
        }
    };

    const handleHeightChange = (val) => {
        if (val == null) return;
        const newH = Number(val);
        setHeight(newH);
        if (keepAspect && aspectRef.current) {
            setWidth(Math.max(MIN_DIM, Math.round(newH * aspectRef.current)));
        }
    };

    const handleToggleAspect = (checked) => {
        setKeepAspect(checked);
        if (checked && aspectRef.current) {
            setHeight(Math.max(MIN_DIM, Math.round(width / aspectRef.current)));
        }
    };

    const handleResize = () => {
        if (!imageSrc) {
            message.error("Please upload an image first");
            return;
        }

        const img = new window.Image();
        img.src = imageSrc;
        img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0, width, height);

            const result = canvas.toDataURL("image/jpeg");
            setProcessedDataUrl(result);
            message.success("Image processed — click Download to save.");
        };
        img.onerror = () => message.error("Failed to process image.");
    };

    const handleRedownload = () => {
        if (!processedDataUrl) {
            message.error("No processed image available. Please Resize first.");
            return;
        }
        downloadImage(
            processedDataUrl,
            `${file ? file.name.replace(/\.[^/.]+$/, "") : "image"}_resized.jpg`
        );
    };

    const handleChangeImage = resetAll;

    return (
        <div style={{ maxWidth: 800, margin: "0 auto" }}>
            <Flex
                vertical
                gap={40}
                align="center"
                style={{
                    padding: "40px 20px",
                }}
            >
                <Flex vertical align="center">
                    <Title level={1}>Resize Image</Title>
                    <Text>Resize images to desired dimensions</Text>
                </Flex>

                {!imageSrc ? (
                    <GeneralUploader
                        showUploadList={false}
                        beforeUpload={() => false}
                        accept="image/*"
                        multiple={false}
                        onChange={handleUploadChange}
                    />
                ) : processed ? (
                    <Card
                        size="small"
                        style={{ padding: 20, textAlign: "center" }}
                    >
                        <Title level={3} style={{ margin: 0 }}>
                            Your image is ready
                        </Title>

                        <Flex vertical gap={14} style={{ marginTop: 20 }}>
                            <Button
                                type="primary"
                                size="default"
                                onClick={handleRedownload}
                                style={{ minWidth: 220 }}
                            >
                                Download
                            </Button>
                            <Button
                                size="default"
                                onClick={handleChangeImage}
                                style={{ minWidth: 220 }}
                            >
                                Start Over
                            </Button>
                        </Flex>
                    </Card>
                ) : (
                    <Flex
                        vertical
                        align="center"
                        gap="middle"
                        style={{ width: "100%" }}
                    >
                        <AntdImage
                            preview={false}
                            src={imageSrc}
                            alt="Preview"
                            style={{ maxHeight: 250 }}
                        />

                        <Divider style={{ margin: "24px 0" }} />

                        <Space wrap align="center">
                            <InputNumber
                                min={MIN_DIM}
                                value={width}
                                onChange={handleWidthChange}
                            />
                            x
                            <InputNumber
                                min={MIN_DIM}
                                value={height}
                                onChange={handleHeightChange}
                            />
                            <Space size="middle" align="center">
                                <Text>Maintain aspect</Text>
                                <Switch
                                    checked={keepAspect}
                                    onChange={handleToggleAspect}
                                />
                            </Space>
                            <Button type="primary" onClick={handleResize}>
                                Resize
                            </Button>
                            <Button onClick={handleChangeImage}>
                                Change Image
                            </Button>
                        </Space>
                    </Flex>
                )}
            </Flex>
        </div>
    );
}
